import React, { useEffect, useState } from 'react';
import { Mic, MicOff, RotateCcw, Pause, Play, Captions, Keyboard, PhoneOff } from 'lucide-react';
import { SEGMENT_LABELS, fmtClock } from '../../../lib/aiInterviewApi';
import { LevelBar } from '../LevelBar';

export const SegmentRail = ({ segments }) => (
  <div className="iv-rail" data-testid="interview-segment-rail">
    {(segments || Object.keys(SEGMENT_LABELS).map((key) => ({ key, status: 'upcoming' }))).map((s) => (
      <div key={s.key} className={s.status === 'done' ? 'is-done' : s.status === 'current' ? 'is-current' : ''} data-testid={`segment-${s.key}`}>
        <i /><span>{SEGMENT_LABELS[s.key] || s.key}</span>
      </div>
    ))}
  </div>
);

export const RoomTimer = ({ remaining, running }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [running]);
  const ms = remaining();
  const low = ms !== null && ms < 180000;
  return (
    <span className={`iv-timer ${low ? 'is-low' : ''}`} data-testid="interview-timer" aria-label="Time remaining">
      {ms === null ? '--:--' : ms < 0 ? `+${fmtClock(-ms)}` : fmtClock(ms)}
    </span>
  );
};

const STATE_TEXT = {
  ready: 'Ready when you are', connecting: 'Connecting…', preparing: 'Preparing your interview…', thinking: 'Thinking…',
  speaking: 'AI speaking…', paused: 'Paused — take your time', muted: 'You\'re muted', typing: 'Type your answer',
  send_failed: 'Connection problem', ending: 'Saving your interview…', error: 'Something went wrong',
};
const ORB = { speaking: 'is-speaking', listening: 'is-listening', thinking: 'is-thinking', connecting: 'is-thinking', preparing: 'is-thinking', ending: 'is-thinking' };

const TypePanel = ({ onSubmit }) => {
  const [text, setText] = useState('');
  const [pasted, setPasted] = useState(false);
  return (
    <div className="iv-type">
      <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} onPaste={() => setPasted(true)}
        placeholder="Type your answer…" aria-label="Typed answer" data-testid="interview-typed-answer-input" />
      <div className="iv-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="iv-btn iv-btn-cta iv-btn-sm" disabled={!text.trim()}
          onClick={() => { onSubmit(text.trim(), pasted); setText(''); setPasted(false); }} data-testid="interview-typed-submit-btn">Send answer</button>
      </div>
    </div>
  );
};

export function AiStage({ s, captions, ctx }) {
  const { phase, ai, youSaid, speechOn, needTap, ttsFailed, meter, error, controls } = s;
  const label = phase === 'listening' ? (speechOn ? 'Listening to you…' : 'Your turn — answer when ready') : STATE_TEXT[phase] || '';
  const showText = captions || ttsFailed;
  return (
    <section className="iv-ai" data-testid="interview-ai-stage">
      <div className={`iv-orb ${ORB[phase] || ''}`} aria-hidden="true" />
      <div className={`iv-state ${phase === 'listening' ? 'is-listening' : ''}`} data-testid="interview-ai-state">{label}</div>
      {phase === 'ready' && (
        <div style={{ maxWidth: 520 }}>
          <p className="iv-caption" style={{ minHeight: 0 }}>{ctx.status === 'in_progress' ? 'Welcome back. Your interview will pick up from your last answer.' : 'You\'re all set. The AI interviewer will greet you and ask one question at a time.'}</p>
          <p className="iv-you-said">Answer out loud. When you pause for a few seconds, your answer is sent automatically, or tap “Done answering”.</p>
          <button type="button" className="iv-btn iv-btn-cta" style={{ marginTop: '1rem' }} onClick={controls.join} data-testid="interview-start-btn">
            {ctx.status === 'in_progress' ? 'Resume interview' : 'Start interview'}
          </button>
        </div>
      )}
      {phase !== 'ready' && ai && (
        <p className="iv-caption" data-testid="interview-caption">{showText ? ai.text : 'Captions are off'}</p>
      )}
      {needTap && <button type="button" className="iv-btn iv-btn-cta" onClick={controls.tapToPlay} data-testid="interview-tap-to-play-btn">Tap to hear the interviewer</button>}
      {phase === 'listening' && (
        <>
          <LevelBar meter={meter} className="iv-level" testId="interview-mic-level" />
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" style={{ marginTop: '0.9rem' }} onClick={controls.doneAnswering} data-testid="interview-done-answering-btn">Done answering</button>
        </>
      )}
      {phase === 'paused' && <button type="button" className="iv-btn iv-btn-cta iv-btn-sm" style={{ marginTop: '0.9rem' }} onClick={controls.togglePause} data-testid="interview-ready-btn">I'm ready</button>}
      {phase === 'muted' && <p className="iv-you-said">Unmute your microphone to answer.</p>}
      {phase === 'typing' && <TypePanel onSubmit={controls.submitTyped} />}
      {phase === 'send_failed' && (
        <div style={{ marginTop: '0.75rem' }}>
          <p className="iv-you-said">{error || 'We could not send your answer.'} Check your connection.</p>
          <button type="button" className="iv-btn iv-btn-cta iv-btn-sm" onClick={controls.retrySend} data-testid="interview-retry-send-btn">Send again</button>
        </div>
      )}
      {phase === 'error' && (
        <div style={{ marginTop: '0.75rem', maxWidth: 520 }}>
          <p className="iv-you-said" data-testid="interview-error">{error === 'media' ? 'We need your camera and microphone. Allow access in your browser settings, then try again.' : error}</p>
          <button type="button" className="iv-btn iv-btn-cta iv-btn-sm" onClick={controls.join} data-testid="interview-retry-start-btn">Try again</button>
        </div>
      )}
      {youSaid && phase !== 'ready' && <p className="iv-you-said" data-testid="interview-you-said">You said: “{youSaid.length > 220 ? `${youSaid.slice(0, 220)}…` : youSaid}”</p>}
    </section>
  );
}

const Ctl = ({ on, onClick, disabled, icon, label, testId, end }) => (
  <button type="button" className={`iv-ctl ${on ? 'is-on' : ''} ${end ? 'is-end' : ''}`} onClick={onClick} disabled={disabled}
    aria-label={label} aria-pressed={on === undefined ? undefined : !!on} data-testid={testId}>
    {icon}<span className="iv-ctl-label">{label}</span>
  </button>
);

export function RoomControls({ s, captions, setCaptions, onEnd }) {
  const { phase, muted, typing, started, controls } = s;
  const active = started && !['ending', 'error'].includes(phase);
  const canRepeat = ['listening', 'muted', 'paused', 'typing'].includes(phase);
  return (
    <div className="iv-controls" data-testid="interview-controls">
      <Ctl on={muted} onClick={controls.toggleMute} disabled={!active} icon={muted ? <MicOff size={18} /> : <Mic size={18} />} label={muted ? 'Unmute' : 'Mute'} testId="interview-mute-btn" />
      <Ctl onClick={controls.repeat} disabled={!active || !canRepeat} icon={<RotateCcw size={18} />} label="Repeat question" testId="interview-repeat-btn" />
      <Ctl on={phase === 'paused'} onClick={controls.togglePause} disabled={!active || !['listening', 'paused'].includes(phase)}
        icon={phase === 'paused' ? <Play size={18} /> : <Pause size={18} />} label={phase === 'paused' ? 'I\'m ready' : 'Give me a moment'} testId="interview-pause-btn" />
      <Ctl on={captions} onClick={() => setCaptions((c) => !c)} icon={<Captions size={18} />} label="Captions" testId="interview-captions-btn" />
      <Ctl on={typing} onClick={controls.toggleTyping} disabled={!active} icon={<Keyboard size={18} />} label="Type instead" testId="interview-type-btn" />
      <Ctl end onClick={onEnd} disabled={!active} icon={<PhoneOff size={18} />} label="Submit & End Interview" testId="candidate-end-interview-btn" />
    </div>
  );
}
